/**
 * The design's "Sidebar" screen, plus the light main column every signed-in screen sits in.
 *
 * Re-derived from `design/Athena Dashboard.html` driven in a browser. The current export's rail
 * runs: brand, a "New knowledge" primary button, a LIBRARY group (All knowledge plus one entry per
 * category, each with a count), a collapsible Settings group holding Install, Tokens and
 * Account & workspace, and an account row with a sign-out control.
 *
 * **The library groups are now real.** The previous export drew six kinds and nothing stored one,
 * so this rail had a single "All knowledge" link. `NoteResponse` carries `category` — rule, flow,
 * guide, knowledge — and the current export draws exactly those four, so the groups filter
 * something.
 *
 * **The counts are counted, not estimated.** `listAllNotes` pages `GET /notes` until the server
 * runs out and the totals come off that set. If the paging ceiling is hit the counts are omitted
 * rather than shown short — a number in a sidebar is a claim, and a claim that is quietly too
 * small is worse than no claim.
 *
 * One thing the export draws that is not reproduced: a **display name** above the email.
 * `AccountResponse` is id, email, workspace_id, workspace_name, created_at, last_login_at.
 * `workspace_name` is not a stand-in — the service derives it from the local part of the email, so
 * stacking the two prints the same string twice. The avatar takes its initials from the email,
 * which is the only name there is.
 *
 * Two things this rail used to have that the export has removed, and so has this: the live
 * "Knowledge in use" strip, and the "Setup guide" link to the Onboarding screen. Absent from the
 * export means removed — see AGENTS.md.
 */

import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback, useState, type ReactNode } from "react";

import * as notesApi from "../lib/api/notes-api";
import * as tokensApi from "../lib/api/tokens-api";
import { useAuth } from "../lib/auth/auth-context";
import { CATEGORIES } from "../lib/ui/categories";
import { SLOW_POLL_MS, usePollTick } from "../lib/ui/use-poll";
import { useResource } from "../lib/ui/use-resource";
import { CategoryIcon } from "./category-icon";

function initials(email: string): string {
  const local = email.split("@")[0] ?? email;
  const letters = local.replace(/[^a-zA-Z]/g, "");
  return (letters.slice(0, 2) || "??").toUpperCase();
}

function NavLink({
  to,
  active,
  count,
  children,
}: {
  to: string;
  active?: boolean;
  count?: number;
  children: ReactNode;
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const onPath = pathname === to || (to !== "/" && pathname.startsWith(`${to}/`));
  const isActive = active ?? onPath;
  return (
    <Link className="sidebar__link" to={to} data-active={isActive}>
      <span className="sidebar__link-label">{children}</span>
      {count !== undefined && <span className="sidebar__link-count">{count}</span>}
    </Link>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const [creating, setCreating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const email = auth.account?.email ?? "";

  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const activeCategory = useRouterState({
    select: (state) => (state.location.search as { category?: string } | undefined)?.category,
  });

  // The counts go stale the moment a note is created or deleted on another screen, and this rail
  // stays mounted across every navigation. Re-asked on the slow tick and on each navigation, which
  // between them covers every way the number can move.
  const tick = usePollTick(SLOW_POLL_MS);

  const loadNotes = useCallback((signal: AbortSignal) => notesApi.listAllNotes({}, signal), []);
  const notes = useResource(loadNotes, [tick, pathname]);

  const loadTokens = useCallback((signal: AbortSignal) => tokensApi.listTokens(signal), []);
  const tokens = useResource(loadTokens, [tick, pathname]);

  const counted = notes.data !== null && notes.data.complete;
  const allNotes = notes.data?.notes ?? [];
  const liveTokens = tokens.data?.filter((token) => token.revoked_at === null).length;

  async function createDraft() {
    setCreating(true);
    try {
      const created = await notesApi.createNote({ title: "Untitled" });
      await navigate({ to: "/knowledge/$knowledgeId", params: { knowledgeId: created.id } });
    } finally {
      setCreating(false);
    }
  }

  async function handleSignOut() {
    setSigningOut(true);
    await auth.signOut();
    // The router guard takes it from here.
  }

  const onLibrary = pathname === "/knowledge" || pathname.startsWith("/knowledge/");
  const inSettingsGroup =
    pathname === "/install" || pathname === "/tokens" || pathname.startsWith("/settings");

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <span className="sidebar__brand-name">Athena</span>
          <span className="sidebar__brand-tag">WORKSPACE</span>
        </div>

        <button
          className="sidebar__new"
          type="button"
          disabled={creating}
          onClick={() => void createDraft()}
        >
          New knowledge
        </button>

        <p className="sidebar__section-label">Library</p>
        <nav className="sidebar__nav" aria-label="Library">
          <NavLink
            to="/knowledge"
            active={onLibrary && activeCategory === undefined}
            count={counted ? allNotes.length : undefined}
          >
            All knowledge
          </NavLink>
          {CATEGORIES.map((meta) => (
            <Link
              className="sidebar__link sidebar__link--category"
              key={meta.value}
              to="/knowledge"
              search={{ category: meta.value }}
              data-active={onLibrary && activeCategory === meta.value}
            >
              <span className="sidebar__link-icon" data-category={meta.value}>
                <CategoryIcon category={meta.value} />
              </span>
              <span className="sidebar__link-label">{meta.plural}</span>
              {counted && (
                <span className="sidebar__link-count">
                  {allNotes.filter((note) => note.category === meta.value).length}
                </span>
              )}
            </Link>
          ))}
        </nav>

        {/*
          The export makes this group collapsible, with Install inside it — Install used to be a
          top-level entry of its own. Nothing depends on the collapse; it is here because the rail
          is otherwise a flat list of seven things.
        */}
        <nav className="sidebar__nav sidebar__group" aria-label="Workspace">
          <button
            className="sidebar__link sidebar__link--toggle"
            type="button"
            aria-expanded={settingsOpen}
            data-active={inSettingsGroup && !settingsOpen}
            onClick={() => {
              setSettingsOpen((open) => !open);
            }}
          >
            <span className="sidebar__link-label">Settings</span>
            <span className="sidebar__caret" aria-hidden="true" data-open={settingsOpen} />
          </button>
          {settingsOpen && (
            <div className="sidebar__subnav">
              <NavLink to="/install">Install</NavLink>
              <NavLink to="/tokens" count={liveTokens}>
                Tokens
              </NavLink>
              <NavLink to="/settings">Account &amp; workspace</NavLink>
            </div>
          )}
        </nav>

        <div className="sidebar__foot">
          <div className="sidebar__account">
            <span className="sidebar__avatar" aria-hidden="true">
              {initials(email)}
            </span>
            {/*
              The design stacks a display name over the email here. There is no display name, and
              the workspace name is not a substitute — the service derives it from the local part
              of the email, so stacking the two would print the same string twice.
            */}
            <span className="sidebar__email" title={email}>
              {email}
            </span>
            <button
              className="sidebar__signout"
              type="button"
              onClick={handleSignOut}
              disabled={signingOut}
            >
              {signingOut ? "Signing out…" : "Sign out"}
            </button>
          </div>
        </div>
      </aside>

      <main className="main">
        <div className="main__inner">{children}</div>
      </main>
    </div>
  );
}
